import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { NavigationItem } from "@/config/navigation/types";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical } from "lucide-react";
import { useState, type PointerEvent } from "react";

interface SortAndToggleableListItemProps {
  item: NavigationItem;
  isToggledOff: boolean;
  onToggle: (toggledOff: boolean) => void;
}

export function SortAndToggleableListItem({ item, isToggledOff, onToggle }: SortAndToggleableListItemProps) {
  const dragControls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);
  const Icon = item.icon;

  function handlePointerDown(event: PointerEvent) {
    event.preventDefault();
    dragControls.start(event);
  }

  return (
    <Reorder.Item
      value={item}
      dragListener={false}
      dragControls={dragControls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      style={{ position: "relative", zIndex: isDragging ? 50 : 0 }}
      className="w-full"
    >
      <Card
        className={`flex w-full flex-row items-center justify-center gap-3 px-3 py-2.5 ${isDragging ? "shadow-lg" : ""}`}
      >
        <button
          type="button"
          onPointerDown={handlePointerDown}
          className="text-muted-foreground hover:text-foreground -ml-1 cursor-grab touch-none active:cursor-grabbing"
          aria-label={item.title()}
        >
          <GripVertical className="h-5 w-5" />
        </button>

        {Icon && (
          <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-lg [&_svg]:h-4 [&_svg]:w-4">
            <Icon />
          </span>
        )}

        <Label className={`mb-0 flex-1 truncate text-sm font-medium ${isToggledOff ? "text-muted-foreground" : ""}`}>
          {item.title()}
        </Label>

        <Switch checked={!isToggledOff} onCheckedChange={(checked) => onToggle(!checked)} />
      </Card>
    </Reorder.Item>
  );
}
